import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
  useSyncExternalStore, ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from './SocketContext';

/* ------------------------------------------------------------------ *
 * Per-service pulse store
 *
 * Kept OUT of React context on purpose. A context value that changes on
 * every check would re-render every subscriber — with 50 services on 30s
 * intervals that is ~1.7 full-grid re-renders per second. An external store
 * lets each row subscribe to its own id and re-render alone.
 * ------------------------------------------------------------------ */

const stamps = new Map<number, number>();
const listeners = new Map<number, Set<() => void>>();

function markUpdated(serviceId: number) {
  stamps.set(serviceId, Date.now());
  listeners.get(serviceId)?.forEach(fn => fn());
}

function subscribe(serviceId: number, cb: () => void) {
  let set = listeners.get(serviceId);
  if (!set) { set = new Set(); listeners.set(serviceId, set); }
  set.add(cb);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) listeners.delete(serviceId);
  };
}

/** Timestamp of this service's most recent check result, or 0. */
export function useServiceStamp(serviceId: number): number {
  return useSyncExternalStore(
    cb => subscribe(serviceId, cb),
    () => stamps.get(serviceId) ?? 0,
    () => 0,
  );
}

/* ------------------------------------------------------------------ */

interface LiveState {
  connected: boolean;
  /** When the last check result arrived, for the freshness clock. */
  lastEventAt: number | null;
}

const LiveContext = createContext<LiveState>({ connected: false, lastEventAt: null });

/**
 * Coalesce refetches. 50 services on 30s intervals fire ~1.7 events/second;
 * at the old 400ms most of them were far enough apart to each trigger a
 * GET /services, which alone approached the API rate limit. Two seconds
 * keeps the dashboard feeling live while capping it at 30 list refetches a
 * minute regardless of fleet size. The per-row pulse (markUpdated) is not
 * debounced, so the "just checked" flash is still immediate.
 */
export const INVALIDATE_DEBOUNCE_MS = 2000;
/** The header clock only needs second resolution. */
const CLOCK_THROTTLE_MS = 1000;

/**
 * One socket subscription for the whole app.
 *
 * Previously every page called `useWebSocketUpdates()`, which registered its
 * own handlers and then tore them down with `socket.off('check:result')` — no
 * handler argument, so that removes EVERY listener for the event, including
 * another page's. Owning the subscription here removes that hazard, coalesces
 * the refetches, and gives the UI something to render when data arrives.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const { socket, connected } = useSocket();
  const qc = useQueryClient();

  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const lastClockUpdate = useRef(0);

  const pending = useRef<{ services: boolean; ids: Set<number> }>({ services: false, ids: new Set() });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!socket) return;

    const flush = () => {
      timer.current = null;
      const { services, ids } = pending.current;
      pending.current = { services: false, ids: new Set() };

      if (services) qc.invalidateQueries({ queryKey: ['services'] });
      ids.forEach(id => {
        qc.invalidateQueries({ queryKey: ['service', id] });
        qc.invalidateQueries({ queryKey: ['checks', id] });
      });
    };

    const schedule = () => {
      if (timer.current === null) timer.current = setTimeout(flush, INVALIDATE_DEBOUNCE_MS);
    };

    const onCheckResult = (data: { service_id: number }) => {
      const now = Date.now();
      markUpdated(data.service_id);

      // Only bump context state once a second, so the provider doesn't
      // re-render its whole subtree on every single check.
      if (now - lastClockUpdate.current >= CLOCK_THROTTLE_MS) {
        lastClockUpdate.current = now;
        setLastEventAt(now);
      }

      pending.current.services = true;
      pending.current.ids.add(data.service_id);
      schedule();
    };

    const onStatusChanged = () => { pending.current.services = true; schedule(); };
    const onGroups = () => qc.invalidateQueries({ queryKey: ['groups'] });
    const onNotifications = () => qc.invalidateQueries({ queryKey: ['notifications'] });
    // Entering or leaving a window rewrites service statuses in bulk.
    const onMaintenance = () => {
      qc.invalidateQueries({ queryKey: ['maintenance'] });
      pending.current.services = true;
      schedule();
    };
    // A status change is what opens or closes an incident.
    const onIncidents = () => {
      qc.invalidateQueries({ queryKey: ['incidents'] });
      qc.invalidateQueries({ queryKey: ['incident-summary'] });
    };

    socket.on('check:result', onCheckResult);
    socket.on('service:status-changed', onStatusChanged);
    socket.on('service:status-changed', onIncidents);
    socket.on('incident:updated', onIncidents);
    socket.on('service:created', onStatusChanged);
    socket.on('service:updated', onStatusChanged);
    socket.on('service:deleted', onStatusChanged);
    socket.on('group:created', onGroups);
    socket.on('group:updated', onGroups);
    socket.on('group:deleted', onGroups);
    socket.on('notification:created', onNotifications);
    socket.on('notification:updated', onNotifications);
    socket.on('notification:deleted', onNotifications);
    socket.on('maintenance:changed', onMaintenance);

    return () => {
      // Pass the handler so only OUR listener is removed.
      socket.off('check:result', onCheckResult);
      socket.off('service:status-changed', onStatusChanged);
      socket.off('service:status-changed', onIncidents);
      socket.off('incident:updated', onIncidents);
      socket.off('service:created', onStatusChanged);
      socket.off('service:updated', onStatusChanged);
      socket.off('service:deleted', onStatusChanged);
      socket.off('group:created', onGroups);
      socket.off('group:updated', onGroups);
      socket.off('group:deleted', onGroups);
      socket.off('notification:created', onNotifications);
      socket.off('notification:updated', onNotifications);
      socket.off('notification:deleted', onNotifications);
      socket.off('maintenance:changed', onMaintenance);
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, [socket, qc]);

  const value = useMemo(() => ({ connected, lastEventAt }), [connected, lastEventAt]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);

/**
 * True for a moment after this service reports — drives the "just updated"
 * highlight. Returns false under reduced-motion so nothing flickers.
 */
export function useServiceFlash(serviceId: number, ms = 1100): boolean {
  const stamp = useServiceStamp(serviceId);
  const [on, setOn] = useState(false);

  useEffect(() => {
    if (!stamp) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    setOn(true);
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [stamp, ms]);

  return on;
}

/** A counter that re-renders once a second, for "updated 4s ago". */
export function useTicker(intervalMs = 1000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}
